import React, { useState, useEffect } from "react";
import i18n, { t } from "@/i18n";
import { Building2, CircleAlert, X } from "lucide-react";
import api from "@/modules/request.module.v2";

import { useBankId } from "@/hooks/useBankId";
import { useCompanySearch } from "@/hooks/syna/useCompanySearch";
import { useCompanyData } from "@/hooks/syna/useCompanyData";
import { useDebounced } from "@/hooks/useDebounced";
import { useCompleteActiveCompanyAccount } from "@/hooks/companies/useCompleteActiveCompanyAccount";
import { useCompanyByOrgNumber } from "@/hooks/companies/useCompanyByOrgNumber";
import { useCreateUnverifiedCompanyAccount } from "@/hooks/companies/useCreateUnverifiedCompanyAccount";
import { ItemList, ItemListColumn, ItemListRow } from "@/components/itemList";
import { maskSsn } from "@/components/helpers/users";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { notify } from "@/components/framework/notify";
import BankIdModal from "@/dumb-components/shared/bankid-modal/bankid-modal";

import bankIdLogo from "@/assets/images/bankid-logo-white.svg";
import synaLogoImg from "@/assets/images/syna-logo.svg";
import { useMe } from "@/hooks/users/useMe";
import LoadingPanel from "@/components/loadingPanel";
import { Checkbox } from "@/components/ui/checkbox";
import { Trans } from "react-i18next";
import CountrySelect from "@/dumb-components/shared/country-select/country-select";
const CompanyBadge = ({ name, orgNumber }) => (
  <div className="gap-sm grid grid-cols-[min-content_auto_min-content]">
    <Building2 size={36} className="text-slate-600" />
    <div className="flex flex-col">
      <div className="text-lg font-semibold">{name}</div>
      <div className="text-slate-500">{orgNumber}</div>
    </div>
  </div>
);

export default function AddCompanyAccountWizard({ onComplete }) {
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounced(searchQuery, 300);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedCountry, setSelectedCountry] = useState("SE");
  const [customOrgNumber, setCustomOrgNumber] = useState("");
  const [customCompanyName, setCustomCompanyName] = useState("");

  const { data: myQuery } = useMe();
  const me = myQuery?.data;

  const {
    data: synaData,
    isFetching,
    error: searchError,
  } = useCompanySearch(debouncedSearchQuery);
  const results = searchError ? [] : synaData?.slice(0, 5) || [];

  const [selectedCompany, setSelectedCompany] = useState(null);

  const { data: companyData, isFetching: isFetchingCompanyData } =
    useCompanyData(selectedCompany?.value);

  const {
    mutateAsync: completeActiveCompanyAccount,
    isPending: isCompletingCompanyAccount,
  } = useCompleteActiveCompanyAccount();
  const {
    mutate: createUnverifiedCompanyAccount,
    isPending: isCreatingUnverifiedCompanyAccount,
  } = useCreateUnverifiedCompanyAccount({
    onSuccess: (mutationResponse) => {
      onComplete?.(mutationResponse?.data?.company);
    },
  });

  useEffect(() => {
    setSelectedIndex(0);
  }, [JSON.stringify(results)]);

  const { initBankIdAuth, bankIdInfo, cancelBankId, resetBankId } = useBankId({
    onBankIdAuthComplete: async ({ orderRef }) => {
      try {
        const result = await completeActiveCompanyAccount({
          orderRef,
          orgNumber: selectedCompany.value,
          email: me?.email,
          userName: me?.name,
          phone: me?.phone,
        });

        onComplete?.(result?.data?.company);
      } catch (error) {
        if (!error?.response?.data?.translationId) {
          notify("error", i18n.t("generic.unknown_error"));
        }
      } finally {
        resetBankId();
      }
    },
  });

  const selectedOrgNumber = selectedCompany?.value;

  const { data: existingCompanyQuery } = useCompanyByOrgNumber(
    selectedOrgNumber,
    {
      enabled: !!selectedOrgNumber,
      retry: false,
    },
  );
  const existingCompany = existingCompanyQuery?.data;

  const isCustomCompany = selectedCountry !== "SE";
  const canCreateCustomCompany =
    !!customCompanyName.trim() &&
    !!customOrgNumber.trim() &&
    !!me?.email &&
    !!me?.name;

  const inputFields = isCustomCompany ? (
    <div className="grid grid-cols-2 gap-2">
      <Input
        autoFocus
        className="w-full text-lg"
        label={t("generic.company_name")}
        value={customCompanyName}
        onChange={(e) => setCustomCompanyName(e.target.value)}
      />
      <Input
        className="w-full text-lg"
        label={t("emissions.subscription-form.organization-number")}
        value={customOrgNumber}
        onChange={(e) => setCustomOrgNumber(e.target.value)}
      />
    </div>
  ) : (
    <Input
      autoFocus
      className="w-full text-lg"
      placeholder={t("company_wizard.step.1.title")}
      value={searchQuery}
      onChange={(e) => setSearchQuery(e.target.value)}
      isLoading={isFetching}
      onKeyDown={(e) => {
        if (e.key === "ArrowDown") {
          setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          setSelectedIndex((prev) => Math.max(prev - 1, 0));
        } else if (e.key === "Enter") {
          if (results[selectedIndex]) {
            setSelectedCompany(results[selectedIndex]);
          }
        }
      }}
    />
  );

  const inputPanel = (
    <div>
      <div className="grid grid-cols-[auto_1fr] items-end gap-2">
        <CountrySelect
          className="text-lg"
          onChange={(country) => setSelectedCountry(country)}
          value={selectedCountry}
        />
        {inputFields}
      </div>
      <div className="flex items-center justify-end gap-2 pt-2">
        <span className="text-slate-400">Powered by</span>
        <div>
          <img src={synaLogoImg} style={{ height: "11px" }} />
        </div>
      </div>
    </div>
  );

  const searchResultsPanel = (
    <div className="max-h-[262px] overflow-auto">
      <ItemList
        isLoading={isFetching && 5}
        hideColumns
        columns={[{ id: "companyName" }]}
        zeroStateRenderer={
          searchQuery?.length > 3
            ? () => t("select.default_no_options")
            : undefined
        }
      >
        {results.map((company, idx) => (
          <ItemListRow
            key={company.value}
            isSelected={idx === selectedIndex}
            onClick={() => setSelectedCompany(company)}
          >
            <ItemListColumn className="flex flex-col">
              <div className="font-semibold">{company.label}</div>
              <div className="text-slate-500">{company.value}</div>
            </ItemListColumn>
          </ItemListRow>
        ))}
      </ItemList>
    </div>
  );

  const customCompanyNotice = isCustomCompany ? (
    <div className="border-box">
      <div className="gap-md flex flex-col">
        <div className="pb-md flex justify-center border-b border-slate-100">
          <CompanyBadge name={customCompanyName} orgNumber={customOrgNumber} />
        </div>
        <div className="gap-md grid text-center text-lg">
          <Trans
            i18nKey="add_company_account_wizard.custom_company_account_notice.body"
            components={[
              <span
                key="unverifiedBadge"
                className="rounded border border-orange-300 bg-orange-100 px-2 font-semibold text-orange-500"
              />,
            ]}
          />
          <Button
            className="justify-self-center"
            size="lg"
            isLoading={isCreatingUnverifiedCompanyAccount}
            disabled={!canCreateCustomCompany}
            onClick={() =>
              createUnverifiedCompanyAccount({
                orgNumber: customOrgNumber.trim(),
                email: me?.email,
                userName: me?.name,
                name: customCompanyName.trim(),
                country: selectedCountry,
                isForeignCompany: true,
              })
            }
          >
            Create unverified company account
          </Button>
        </div>
      </div>
    </div>
  ) : null;

  const companyPicker = selectedCompany ? (
    <div className="border-box">
      <div className="gap-sm grid grid-cols-[min-content_auto_min-content]">
        <Building2 size={36} className="text-slate-600" />
        <div className="flex flex-col">
          <div className="text-lg font-semibold">{selectedCompany.label}</div>
          <div className="text-slate-500">{selectedCompany.value}</div>
        </div>
        <Button
          disabled={
            isCompletingCompanyAccount || isCreatingUnverifiedCompanyAccount
          }
          variant="ghost"
          onClick={() => setSelectedCompany(null)}
        >
          <X /> {t("generic.form.change")}
        </Button>
      </div>
    </div>
  ) : (
    <>
      {inputPanel}
      {customCompanyNotice ?? searchResultsPanel}
    </>
  );

  const existingCompanyNotice = existingCompany ? (
    <div className="border-box gap-md items-center">
      <div className="pb-sm flex items-center justify-center gap-2 text-lg font-semibold">
        <CircleAlert className="text-primary" />{" "}
        {t("company_wizard.alreay_exists.title")}
      </div>
      <div className="flex">
        <ul className="gap-md flex list-disc flex-col px-6 text-lg">
          <li>
            <Trans
              i18nKey="company_wizard.alreay_exists.hint_1"
              values={{
                companyName: existingCompany.name,
                userEmail: me.email,
              }}
            />
          </li>
          <li>
            <Trans
              i18nKey="company_wizard.alreay_exists.hint_2"
              values={{ companyName: existingCompany.name }}
            />
          </li>
        </ul>
      </div>
    </div>
  ) : null;

  const repList =
    companyData?.allowedRepresentativesToSign?.length ||
    isFetchingCompanyData ? (
      <div className="space-y-md">
        {isCompletingCompanyAccount ? (
          <div className="border-box">
            <LoadingPanel size={48}>
              {t("add_company_account_wizard.creating_company_account")}
            </LoadingPanel>
          </div>
        ) : (
          <ItemList
            columns={[
              { id: "name", label: "Name", size: "2fr" },
              { id: "ssn", label: "SSN" },
              { id: "roles", label: "Roles" },
            ]}
            rightAlignLastColumn
            isLoading={isFetchingCompanyData && 2}
          >
            {companyData?.allowedRepresentativesToSign?.map((rep) => (
              <ItemListRow key={rep.ssn}>
                <ItemListColumn>{rep.name}</ItemListColumn>
                <ItemListColumn>{maskSsn(rep.ssn)}</ItemListColumn>
                <ItemListColumn>{rep.roles.join(", ")}</ItemListColumn>
              </ItemListRow>
            ))}
          </ItemList>
        )}
        <div>
          <div className="border-box items-center">
            <div className="gap-sm grid max-w-[400px] grid-cols-[20px_1fr]">
              <Checkbox
                size="lg"
                id="terms"
                checked={termsAccepted}
                onCheckedChange={setTermsAccepted}
              />
              <label htmlFor="terms" className="text-md font-normal">
                <Trans
                  i18nKey="add_company_account_wizard.terms.label"
                  components={[
                    <a
                      key="a"
                      className="text-primary font-semibold hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                      href={
                        !i18n?.language?.startsWith("sv")
                          ? "https://invono.se/en-US/legal/company-account-agreement"
                          : "https://invono.se/sv/legal/company-account-agreement"
                      }
                    ></a>,
                  ]}
                />
              </label>
            </div>
          </div>
        </div>
        <Button
          size="lg"
          onClick={() =>
            initBankIdAuth(
              api.post("/companies/active/init", {
                orgNumber: selectedCompany.value,
                ssn: me?.ssn,
                email: me?.email,
                userName: me?.name,
                name: selectedCompany.label,
              }),
            )
          }
          className="w-full"
          disabled={isCompletingCompanyAccount || !termsAccepted}
        >
          <div className="flex items-center gap-4">
            <img src={bankIdLogo} width={36} />
            {t("company_wizard.step.4.btn.verify_with_bankid")}
          </div>
        </Button>
        {me?.invonoAdmin && (
          <Button
            disabled={
              isCompletingCompanyAccount || isCreatingUnverifiedCompanyAccount
            }
            isLoading={isCreatingUnverifiedCompanyAccount}
            size="lg"
            className="w-full"
            variant="destructive"
            onClick={() =>
              createUnverifiedCompanyAccount({
                orgNumber: selectedCompany.value,
                name: selectedCompany.label,
                email: me?.email,
                userName: me?.name,
                country: "SE",
                isForeignCompany: false,
              })
            }
          >
            Invono admin: Create and verify
          </Button>
        )}
        <BankIdModal
          isOpen={!!bankIdInfo.status && bankIdInfo.status !== "complete"}
          bankIdInfo={bankIdInfo}
          onCancel={cancelBankId}
        />
      </div>
    ) : null;

  return (
    <div>
      <div className="gap-sm flex flex-col">
        {companyPicker}
        {existingCompanyNotice ?? repList}
      </div>
    </div>
  );
}

export { AddCompanyAccountWizard };
